import { useState } from 'react';
import { api } from '../lib/api.js';

// ─── Admin Credential Reset (password + PIN) ─────────────────────────────────
// Rendered inside the Security ProfileSection when a manager+ is viewing
// someone else's profile. Two actions: reset password to the default
// (forces target user to change on next login) and regenerate PIN
// (returns the new PIN inline so the manager can hand it off).
export function AdminCredentialReset({ currentUser, target }) {
  const [busy, setBusy] = useState(null);   // 'pw' | 'pin-set' | 'pin-gen' | null
  const [result, setResult] = useState(null); // { kind, message, pin? }
  const [error, setError] = useState(null);
  const [customPin, setCustomPin] = useState('');
  const headers = { 'x-user-id': String(currentUser.id) };

  async function resetPassword() {
    if (!confirm(`Reset ${target.name}'s password? A new temporary password will be generated.`)) return;
    setBusy('pw'); setError(null); setResult(null);
    try {
      const r = await api('POST', `/api/admin/users/${target.id}/reset-password`, {}, headers);
      setResult({ kind: 'pw', message: `Temporary password: ${r.temp_password}. ${target.name} will be forced to change it on next login. Share it over a secure channel.` });
    } catch (e) {
      setError(e.message || 'Reset failed.');
    } finally {
      setBusy(null);
    }
  }

  async function setSpecificPin() {
    if (!/^\d{4}$/.test(customPin)) { setError('PIN must be exactly 4 digits.'); return; }
    if (!confirm(`Set ${target.name}'s kiosk PIN to ${customPin}? Their old PIN will stop working immediately.`)) return;
    setBusy('pin-set'); setError(null); setResult(null);
    try {
      const r = await api('POST', `/api/admin/users/${target.id}/reset-pin`, { pin: customPin }, headers);
      setResult({ kind: 'pin', pin: r.pin, message: `Kiosk PIN for ${target.name}:` });
      setCustomPin('');
    } catch (e) {
      setError(e.message || 'Reset failed.');
    } finally {
      setBusy(null);
    }
  }

  async function generatePin() {
    if (!confirm(`Generate a new random kiosk PIN for ${target.name}? Their old PIN will stop working immediately.`)) return;
    setBusy('pin-gen'); setError(null); setResult(null);
    try {
      const r = await api('POST', `/api/admin/users/${target.id}/reset-pin`, {}, headers);
      setResult({ kind: 'pin', pin: r.pin, message: `New PIN for ${target.name}:` });
    } catch (e) {
      setError(e.message || 'Reset failed.');
    } finally {
      setBusy(null);
    }
  }

  const pinBusy = busy === 'pin-set' || busy === 'pin-gen';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: '#212121' }}>Email-login password</div>
        <button onClick={resetPassword} disabled={busy === 'pw'} className="btn-secondary" style={{ fontSize: 12 }}>
          {busy === 'pw' ? 'Resetting…' : 'Reset to default'}
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 13, color: '#212121', fontWeight: 600 }}>Kiosk PIN</div>
        <div style={{ fontSize: 11, color: '#757575' }}>
          Enter a 4-digit PIN (matching their Square clock-in PIN works well), or generate one at random. Each PIN must be unique across staff.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={customPin}
                 onChange={(e) => setCustomPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                 inputMode="numeric" pattern="\d*" maxLength={4} placeholder="1234"
                 style={{ width: 90, padding: '6px 10px', border: '1px solid #d0d4d9', borderRadius: 6,
                          fontFamily: 'monospace', fontSize: 16, letterSpacing: '0.1em' }} />
          <button onClick={setSpecificPin} disabled={pinBusy || customPin.length !== 4} className="btn-secondary" style={{ fontSize: 12 }}>
            {busy === 'pin-set' ? 'Saving…' : 'Use this PIN'}
          </button>
          <span style={{ fontSize: 11, color: '#9e9e9e' }}>or</span>
          <button onClick={generatePin} disabled={pinBusy} className="btn-secondary" style={{ fontSize: 12 }}>
            {busy === 'pin-gen' ? 'Generating…' : 'Generate random'}
          </button>
        </div>
      </div>
      {result && result.kind === 'pin' && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{result.message}</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'monospace', color: '#14532d', letterSpacing: '0.1em' }}>{result.pin}</div>
          </div>
          <button onClick={() => navigator.clipboard.writeText(result.pin)}
                  style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #bbf7d0', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
            Copy
          </button>
        </div>
      )}
      {result && result.kind === 'pw' && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 10, fontSize: 12, color: '#166534' }}>
          {result.message}
        </div>
      )}
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 10, fontSize: 12, color: '#991b1b' }}>
          {error}
        </div>
      )}
    </div>
  );
}
